import React from 'react';
import styled from 'styled-components';
import { Body7, Container } from '@ui';
import { convertHexToRGBA } from '@utils/usefulFuncs';
import { useTagSearchResults } from '@hooks/features/useStockSearch';

const Wrapper = styled.div`
  //
`;
const InputWrapper = styled.div`
  position: relative;
  min-width: 200px;
  flex: 1;
`;
const InputStyling = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  color: ${({ theme }) => theme.themeColors.text};
  background-color: transparent;
  border-radius: 6px;
  width: 100%;
  padding: 8px;
  cursor: text;

  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;

  input {
    min-width: 200px;

    font-size: 14px;
    border: none !important;
    outline: none;
    line-height: 18px;
  }
`;
const SelectableTagsWrapper = styled.div`
  position: relative;
  width: 100%;
  display: ${({ isFocused }) => (isFocused ? 'block' : 'none')};
`;
const SelectableTagWrapper = styled.div`
  color: ${({ theme, isSelected }) => (isSelected ? theme.themeColors.buttonText : theme.themeColors.text)};
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.themeColors.primaryCtaButton : convertHexToRGBA(theme.themeColors.appBackground, 0.3)};
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  cursor: pointer;

  padding: 8px 12px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  transition: all 150ms;

  * {
    color: ${({ theme, isSelected }) => (isSelected ? theme.themeColors.buttonText : theme.themeColors.text)};
  }

  &:hover {
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.75)};
    color: ${({ theme }) => theme.themeColors.buttonText};

    * {
      color: ${({ theme }) => theme.themeColors.buttonText};
    }
  }
`;
const TruncateText = styled.span`
  width: 100%;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const SelectableTagsPositionWrapper = styled.div`
  position: absolute;
  top: 14px;
  left: -8px;
  right: -8px;
  max-height: 60vh;
  overflow: auto;
  z-index: 5;
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.25);

  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-top-left-radius: 1px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  border-top-right-radius: 1px;
`;

let queuedBlur = null;

const queueBlur = (cb) => {
  queuedBlur = setTimeout(() => {
    cb();
  }, 200);
};

// function formatTagResponseData(data) {
//   return {
//     ...data,
//     id: data.doc_tag_id || data.id,
//   };
// }

const SelectableTag = ({ value, onClick, isSelected }) => {
  const { company_id, name, symbol } = value;

  const handleClick = () => {
    onClick(value);
  };

  const text = symbol === null ? name : `${symbol} - ${name}`;
  return (
    <SelectableTagWrapper isSelected={isSelected} onClick={handleClick}>
      <TruncateText>
        <Body7>{text}</Body7>
      </TruncateText>
    </SelectableTagWrapper>
  );
};

export const SelectableTags = ({ query, value, isQuerying, onClick, isFocused, selectResultIndex }) => {
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    try {
      const inBounds = (scrollBounds, elHeight, elPosY) => {
        const lower = scrollBounds[0];
        const upper = scrollBounds[1];
        return lower <= elPosY && elPosY <= upper;
      };
      const selectedRow = scrollRef.current.children[selectResultIndex];
      const scrollBoxHeight = scrollRef.current.offsetHeight;
      const scrollPos = scrollRef.current.scrollTop;
      const selectedElementHeight = selectedRow.offsetHeight;
      const selectedElementPosY = selectedRow.offsetTop;

      const scrollBounds = [scrollPos, scrollBoxHeight + scrollPos];
      if (!inBounds(scrollBounds, selectedElementHeight, selectedElementPosY)) {
        selectedRow.scrollIntoView({ alignToTop: false, behavior: 'smooth', inline: 'nearest' });
      }
    } catch (e) {
      // console.error(e);
    }
  }, [scrollRef, selectResultIndex, value]);

  if (!value || !query || query === '' || isQuerying) return null;

  return (
    <SelectableTagsWrapper isFocused={isFocused}>
      <SelectableTagsPositionWrapper ref={scrollRef}>
        {value?.map((r, i) => (
          <SelectableTag key={r} value={r} onClick={onClick} isSelected={selectResultIndex === i} />
        ))}
        {value?.length === 0 ? (
          <SelectableTagWrapper onClick={() => null}>
            <Container vertical={4}>
              <Body7>No results for "{query}"</Body7>
            </Container>
          </SelectableTagWrapper>
        ) : null}
      </SelectableTagsPositionWrapper>
    </SelectableTagsWrapper>
  );
};

const tagSearchOptions = {
  showCompanyTags: true,
};
export const AllCompanySearch = ({ onSelect }) => {
  const inputRef = React.useRef(null);
  const [inputValue, setInputValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  const [selectResultIndex, setSelectResultIndex] = React.useState(null);
  const [isQuerying, selectableTags, activeQuery] = useTagSearchResults(inputValue, tagSearchOptions);

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value === ' ') {
      return;
    }
    setInputValue(value);

    if (!isFocused) {
      setIsFocused(true);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleBlur = () => {
    queueBlur(() => setIsFocused(false));
    // need delay otherwise clicks are missed
  };

  const handleTagSelect = (v) => {
    onSelect(v);
    setInputValue('');
    setIsFocused(false);
    inputRef.current.blur();
    if (queuedBlur) {
      clearTimeout(queuedBlur);
    }
  };

  const handleInputClick = () => {
    inputRef?.current?.focus();
  };

  React.useEffect(() => {
    // always reset index on input change
    setSelectResultIndex(0);
  }, [inputValue]);

  React.useEffect(() => {
    const handleSelectSearchResultsKeyBinds = (e) => {
      const key = e?.key?.toLowerCase();

      if (['enter'].includes(key)) {
        const selectedTicker = selectableTags[selectResultIndex];
        if (selectedTicker && selectedTicker.length > 0) {
          handleTagSelect(selectedTicker);
        }
      }
    };
    document.addEventListener('keydown', handleSelectSearchResultsKeyBinds);
    return () => {
      document.removeEventListener('keydown', handleSelectSearchResultsKeyBinds);
    };
  }, [inputValue, selectableTags, selectResultIndex]);

  return (
    <Wrapper>
      <InputStyling onClick={handleInputClick}>
        <InputWrapper>
          <input
            ref={inputRef}
            placeholder="Search for a Company / Tag"
            value={inputValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleInputChange}
          />
          <SelectableTags
            query={activeQuery}
            value={selectableTags}
            isQuerying={isQuerying}
            onClick={handleTagSelect}
            selectResultIndex={selectResultIndex}
            isFocused={isFocused}
          />
        </InputWrapper>
      </InputStyling>
    </Wrapper>
  );
};
